// How the outreach log reads on the Popular guests tab: the chip, the line
// under the stats, and the labels the history dialog shares with them. Pure
// formatting, no Firebase, so it can be tested on its own.

// "2h ago" / "3d ago" / "Aug 25" for the stats line and the outreach line.
export function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** A moment, the way the history reads it: "Sep 3, 2:59 PM". */
export function formatSentAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const KIND_LABELS = {
  earn: "Earn nudge",
  invite: "Event invite",
  "earn+invite": "Earn nudge + invite",
  offer: "Fee offer",
};

/** What a message was about, in the operator's words. */
export function kindLabel(kind) {
  return KIND_LABELS[kind] || "Message";
}

/** Which channels a message actually went out on. */
export function channelsLabel(channels, reached = true) {
  const parts = [];
  if (channels?.sms) parts.push("text");
  if (channels?.push) parts.push("push");
  if (!reached || parts.length === 0) return "not delivered";
  return parts.join(" + ");
}

/**
 * The chip on a ranking row. Two states only: someone has reached this person
 * from the tab, or nobody has. The "when" rides on the chip because that is
 * what an operator scans the list for.
 */
export function outreachChip(outreach) {
  if (!outreach || !outreach.count) {
    return {
      label: "Not messaged",
      className: "border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400",
    };
  }
  const when = timeAgo(outreach.lastAt);
  return {
    label: when ? `Messaged ${when}` : "Messaged",
    className: "bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-300",
  };
}

/**
 * Who sent the last message, as the line says it.
 *
 * A host reading their own Popular tab never learns who else reached a member:
 * the server strips the names from their rows and says only whether the last
 * one was theirs. So a host line says "by you" or nothing at all, whatever the
 * row happens to carry. Operators see the name, marked when a host sent it.
 */
function lastSender(outreach, forHost) {
  if (forHost) return outreach.lastByYou ? " by you" : "";
  const fromHost = outreach.lastByRole === "host";
  if (outreach.lastByName) return ` by ${outreach.lastByName}${fromHost ? " (host)" : ""}`;
  return fromHost ? " by a host" : "";
}

// The kinds a host may read on their own rows: the ones a host can send too.
// A fee offer or a payout notice is Gist booking or paying someone, which is
// not a host's business, so on a host line those say nothing about what it was.
const HOST_KINDS = new Set(["earn", "invite", "earn+invite"]);

/**
 * The line under the stats: how many times, the last one (when, by whom, about
 * what) and, when the tab is open on an event, whether any of it was about this
 * one. Null when there is nothing to say. `forHost` is the host's view of it.
 *
 * A host line names no other event. The server already drops the kind and the
 * event of a message that was not about the host's own event, and this is the
 * second wall: event names often carry another host's name, and "fee offer at
 * Priya's Supper Club" would tell a host who else booked this member and for
 * what. So a host line only ever says "this event", or nothing.
 */
export function outreachLine(outreach, eventID = null, { forHost = false } = {}) {
  if (!outreach || !outreach.count) return null;
  const when = timeAgo(outreach.lastAt) || "just now";
  const by = lastSender(outreach, forHost);
  const parts = [];
  if (outreach.count === 1) parts.push(`Messaged ${when}${by}`);
  else parts.push(`Messaged ${outreach.count} times`, `last ${when}${by}`);
  if (!forHost || HOST_KINDS.has(outreach.lastKind)) parts.push(kindLabel(outreach.lastKind).toLowerCase());

  const lastWasThisEvent = Boolean(eventID) && outreach.lastEventID === eventID;
  if (forHost) {
    if (lastWasThisEvent) parts.push("this event");
  } else if (outreach.lastEventName) {
    parts.push(lastWasThisEvent ? "this event" : outreach.lastEventName);
  }
  // The last message already named this event; otherwise say whether any
  // earlier one did, because "messaged" alone does not mean "about tonight".
  if (eventID && !lastWasThisEvent) {
    parts.push(outreach.thisEvent?.count
      ? `about this event ${timeAgo(outreach.thisEvent.lastAt) || "just now"}`
      : "not yet about this event");
  }
  return parts.join(" · ");
}

/**
 * "4 of 8 joined after looking (50%)": of the people who opened this profile
 * before joining the event, how many then joined. Null when nobody looked
 * before joining, since 0 of 0 says nothing.
 */
export function joinRateText(stats) {
  const looked = stats?.nonMemberViewers || 0;
  if (!looked) return null;
  const joined = stats.convertedViewers || 0;
  return `${joined} of ${looked} joined after looking (${Math.round((100 * joined) / looked)}%)`;
}

/**
 * The stats line on a ranking row: how many different people opened their
 * profile, how often, across how many events, how many of the ones who looked
 * before joining then joined, and when last. Shared by the operator tab and the
 * host view. Ranked by join rate, that part leads, since it is the order.
 */
export function statsLine(stats, scope, sort = "viewers") {
  const s = stats || {};
  const joinRate = joinRateText(s);
  const parts = [
    `${s.distinctViewers || 0} ${s.distinctViewers === 1 ? "person" : "people"} viewed`,
    `${s.views || 0} ${s.views === 1 ? "view" : "views"}`,
  ];
  // In event scope every row is "1 event" by definition, which is noise.
  if (scope !== "event") parts.push(`${s.eventCount || 0} ${s.eventCount === 1 ? "event" : "events"}`);
  if (joinRate && sort === "joinRate") parts.unshift(joinRate);
  else if (joinRate) parts.push(joinRate);
  const last = timeAgo(s.lastViewedAt);
  if (last) parts.push(`last ${last}`);
  return parts.join(" · ");
}
